import type { Facture } from '@shared/types/Facture';
import type { Tenant } from '@shared/types/Tenant';
import type { ConditionsReglement } from '@shared/types/Tiers';
import { fmtFCFA, fmtDate, esc } from '@presentation/shell/format';
import {
  brandCenterStripHtml,
  brandFontStyleBlock,
  brandHeaderTextHtml,
  brandRootClass,
  brandRootStyleAttr,
  bulletinHeaderLogoHtml,
  bulletinRightLogoHtml,
  documentThemeFor,
} from '@presentation/print/documentBrand';
import { montantEnLettresFcfa } from '@shared/utils/montantEnLettres';

/** Coordonnées tiers pour impression (client ou fournisseur). */
export interface FactureTiersPrint {
  code?: string;
  raison_sociale?: string;
  nif?: string;
  rccm?: string;
  adresse?: string;
  ville?: string;
  telephone?: string;
  email?: string;
  conditions_reglement?: ConditionsReglement | string;
  iban?: string;
}

const CONDITIONS_LIBELLE: Record<string, string> = {
  comptant: 'Comptant',
  '30j': '30 jours nets',
  '60j': '60 jours nets',
  '90j': '90 jours nets',
};

function libelleConditions(c?: string): string {
  if (!c) return '30 jours nets';
  return CONDITIONS_LIBELLE[c] ?? c;
}

/** Le taux affiché suit les lignes : une facture peut mêler 18 %, 10 %, 5 % ou exonéré. */
function libelleTauxTva(facture: Facture): string {
  const taux = [...new Set(facture.lignes.map((l) => l.taux_tva))].sort((a, b) => a - b);
  const unique = taux.length === 1 ? taux[0] : undefined;
  if (unique === undefined) return 'TVA (taux multiples)';
  if (unique === 0) return 'TVA (exonéré)';
  const pct = (unique * 100).toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
  return `TVA ${pct} %`;
}

export function buildFactureHTML(
  facture: Facture,
  tenant: Tenant | null,
  tiers?: FactureTiersPrint | null,
): string {
  const titre = facture.type === 'achat' ? 'FACTURE D’ACHAT' : 'FACTURE';
  const achat = facture.type === 'achat';
  const libelleTiers = achat ? 'Fournisseur' : 'Client';
  const lignes = facture.lignes
    .map(
      (l) => `<tr>
        <td>${esc(l.description)}</td>
        <td class="num">${l.quantite}</td>
        <td class="num">${esc(fmtFCFA(l.prix_unitaire))}</td>
        <td class="num">${esc(fmtFCFA(l.montant_ht))}</td>
        <td class="num">${esc(fmtFCFA(l.montant_tva))}</td>
        <td class="num">${esc(fmtFCFA(l.montant_ttc))}</td>
      </tr>`,
    )
    .join('');

  const emitteurCoord = [
    tenant?.adresse,
    tenant?.ville,
    tenant?.telephone ? `Tél. ${tenant.telephone}` : '',
    tenant?.email,
  ]
    .filter(Boolean)
    .join(' · ');

  const clientNom = tiers?.raison_sociale || tiers?.code || facture.client_id || '—';
  const clientNif = tiers?.nif?.trim() || '';
  const clientBloc = [
    clientNom,
    tiers?.adresse,
    [tiers?.ville, tiers?.telephone].filter(Boolean).join(' · '),
    clientNif ? `NIF ${clientNif}` : `NIF ${libelleTiers.toLowerCase()} : à compléter`,
    tiers?.rccm ? `RCCM ${tiers.rccm}` : '',
    tiers?.email,
  ]
    .filter(Boolean)
    .join('<br>');

  const conditions = libelleConditions(tiers?.conditions_reglement);
  const libelleTva = libelleTauxTva(facture);
  const ibanEmetteur =
    (tenant?.settings as { iban?: string } | undefined)?.iban ||
    (tenant?.parametres as { iban?: string } | undefined)?.iban ||
    '';

  const theme = documentThemeFor(tenant);
  const emitteurNom = tenant?.raison_sociale || tenant?.nom || 'Entreprise';

  return `<div class="bulletin${brandRootClass(theme)}" id="facture-print"${brandRootStyleAttr(theme)}>
    ${brandFontStyleBlock(theme)}${brandCenterStripHtml(theme, emitteurNom)}<div class="bulletin-header">
      <div>
        ${bulletinHeaderLogoHtml(40, theme, emitteurNom)}
        <div class="company">${esc(emitteurNom)}</div>
        <div class="meta">NIF ${esc(tenant?.nif || '—')} · RCCM ${esc(tenant?.rccm || '—')}</div>
        <div class="meta">${esc(emitteurCoord || tenant?.ville || '')}${brandHeaderTextHtml(theme)}</div>
      </div>
      <div style="text-align:right;">
        ${bulletinRightLogoHtml(theme, emitteurNom)}<div class="company">${titre}</div>
        <div class="meta">${esc(facture.numero)} · ${esc(fmtDate(facture.date))}</div>
        <div class="meta">Échéance ${esc(fmtDate(facture.date_echeance))}</div>
      </div>
    </div>
    <div class="bulletin-meta-section">
      <div class="group"><h4>${libelleTiers}</h4>
        <div class="val" style="line-height:1.45;font-size:12px;">${clientBloc}</div>
      </div>
      <div class="group"><h4>Pièce</h4>
        <div class="row"><span class="lbl">Objet</span><span class="val">${esc(facture.objet || '—')}</span></div>
        <div class="row"><span class="lbl">Réf. tiers</span><span class="val mono">${esc(facture.client_id || '—')}</span></div>
        <div class="row"><span class="lbl">Statut</span><span class="val">${esc(facture.statut)}</span></div>
        <div class="row"><span class="lbl">Règlement</span><span class="val">${esc(conditions)}</span></div>
      </div>
    </div>
    <table class="bulletin-table">
      <thead><tr><th>Désignation</th><th class="num">Qté</th><th class="num">P.U.</th><th class="num">HT</th><th class="num">TVA</th><th class="num">TTC</th></tr></thead>
      <tbody>${lignes}</tbody>
    </table>
    <div class="bulletin-totals">
      <div>Total HT <strong>${esc(fmtFCFA(facture.total_ht))}</strong></div>
      <div>${esc(libelleTva)} <strong>${esc(fmtFCFA(facture.total_tva))}</strong></div>
      <div>Total TTC <strong>${esc(fmtFCFA(facture.total_ttc))}</strong></div>
    </div>
    <p style="margin:14px 0 8px;font-size:12px;"><strong>Arrêté la présente facture à la somme de :</strong><br>${esc(montantEnLettresFcfa(facture.total_ttc))}.</p>
    <div style="margin-top:12px;font-size:11px;color:var(--ardoise,#2C4A5A);line-height:1.5;">
      <div><strong>Conditions de règlement :</strong> ${esc(conditions)}. Escompte pour paiement comptant : néant sauf accord écrit.</div>
      <div><strong>Pénalités de retard :</strong> taux légal en vigueur (CGI Gabon), exigibles de plein droit sans mise en demeure préalable.</div>
      ${ibanEmetteur ? `<div><strong>IBAN émetteur :</strong> <span class="mono">${esc(ibanEmetteur)}</span></div>` : '<div><strong>Paiement :</strong> virement bancaire ou espèces — coordonnées IBAN à compléter dans Paramètres entreprise.</div>'}
      <div style="margin-top:6px;"><em>Facturation électronique normalisée (Loi de finances 2026) : conservez ce document et les exports e-TVA/DGI associés.</em></div>
    </div>
    <div style="display:flex;justify-content:space-between;gap:24px;margin-top:28px;font-size:12px;">
      <div style="flex:1;">
        <div style="font-weight:700;margin-bottom:36px;">Bon pour accord client</div>
        <div>Date : _______________</div>
        <div style="margin-top:8px;">Signature / cachet :</div>
      </div>
      <div style="flex:1;text-align:right;">
        <div style="font-weight:700;margin-bottom:36px;">La Direction</div>
        <div>Signature / cachet émetteur</div>
      </div>
    </div>${theme.active && theme.footerText ? `\n    <div class="bulletin-footer" style="margin-top:14px;">${esc(theme.footerText)}</div>` : ''}
  </div>`;
}
